import { Fragment } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  deleteWithUndo,
  flushBlobs,
  listLabel,
  styled,
  takenNames,
  ToucanButton,
  ToucanDangerZone,
  ToucanFilePicker,
  ToucanNewThingDialog,
  ToucanSpinner,
  showError,
  useBlobUrl,
  useEditedText,
} from "toucan";
import { enrich } from "../enrich.ts";
import {
  addList,
  archiveItem,
  deleteItem,
  isArchived,
  isWebAddress,
  itemFacts,
  refetchItem,
  RESERVED_LIST_NAMES,
  setItemList,
  setItemNote,
  setItemPaywalled,
  setItemThumb,
  setItemTitle,
  unarchiveItem,
} from "../model.ts";
import { go, libraryPath, takeJustSaved } from "../routes.ts";
import { lists, store } from "../state.ts";
import type { FileOption } from "toucan";
import type { Item } from "../model.ts";

const Page = styled("section")`
  display: grid;
  /* Each row at the height of its content. The page fills the screen so the
     canvas reaches the bottom, and this puts the spare room below the rows
     rather than between them. */
  align-content: start;
  gap: 0.9rem;
  padding: 1rem 1rem 3rem;
  min-height: 100%;
  /* The same warm canvas the library sits on. */
  background: var(--toucan-canvas);

  /* The address, a link to the site that can also be selected and copied,
     with Fetch again beside it. */
  .top {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .address {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-all;
    color: var(--pico-primary);
  }

  /* The lookup's progress shows on the Fetch again button, not on a line of
     its own, which would move the fields below as it came and went. */
  .top button {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }

  /* The ring at the size of the word beside it. */
  .top .looking {
    --toucan-spinner-size: 0.85rem;
  }

  input,
  textarea {
    margin: 0;
  }

  /* The form's own gap does the spacing between fields, so a fieldset adds
     none of its own. */
  fieldset {
    display: grid;
    gap: 0.9rem;
    margin: 0;
    padding: 0;
  }

  /* A section's label, with a little more space below than the grid gap. */
  legend {
    margin-bottom: 0.25rem;
    font-weight: 700;
  }

  /* The list picker and the switches are two small rows, so they sit closer
     than the page's full gap. */
  fieldset.filed {
    gap: 0.45rem;
  }

  /* The card's own picture at card size; see EditPage for why no larger. */
  .thumb {
    display: block;
    width: 100%;
    max-width: 22rem;
    aspect-ratio: 3 / 2;
    object-fit: cover;
    /* The same crop as the card's. */
    object-position: top;
    border-radius: var(--pico-border-radius);
    background: var(--toucan-surface-sunken);
  }

  /* The item's dates, read-only, with labels and values in two columns. */
  .facts {
    display: grid;
    grid-template-columns: max-content max-content;
    gap: 0.2rem 1rem;
    margin: 0;
    font-size: 0.95rem;
    color: var(--toucan-muted);
  }

  .facts dt {
    font-weight: 700;
  }

  .facts dd {
    margin: 0;
  }

  /* A switch and its label on one line. */
  .switch {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
  }

  .switch input {
    margin: 0;
  }

  /* The two switches side by side, wrapping on a narrow screen rather than
     shrinking. */
  .switches {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem 1.5rem;
  }

  /* On a wide screen, pull the switches up to the list picker and add space
     below them, to separate the Picture section. */
  @media (min-width: 720px) {
    .switches {
      margin: -0.35rem 0 0.75rem;
    }
  }
`;

/**
 * Editing a saved item: its title, note, list, switches and picture, with its
 * address at the top as the thing to check a fetch's answer against.
 *
 * The picture is shown at card size, with no full-bleed hero, since a phone
 * at DPR 3 would need an image about 1000px wide per item where a card needs
 * 400: an order of magnitude more storage.
 */
export function EditPage({ item }: { item: Item }) {
  const { id, fetchedAt } = item;
  // Set from the first render, since the effect below starts the lookup only
  // after the first paint, and the button should not say Fetch again first.
  const [finding, setFinding] = useState(fetchedAt === undefined);
  // Counts presses of Fetch again, since an item whose first ask settled
  // nothing has no fetch date for the press to clear.
  const [asked, setAsked] = useState(0);
  const [newList, setNewList] = useState(false);
  const noteField = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (takeJustSaved(id)) {
      noteField.current?.focus();
    }
  }, [id]);
  // An item not yet fetched is looked up as soon as this page shows it.
  useEffect(() => {
    if (fetchedAt !== undefined) {
      // Already fetched, or the answer has just been stored and re-run this.
      setFinding(false);
      return undefined;
    }
    let showing = true;
    setFinding(true);
    // A page that turns out to be one already saved folds into it, and the
    // shell follows the item this one was folded into.
    void enrich(store(), item, Date.now()).then((settled) => {
      // Upload the new blobs, even if the page has gone; see `main` in
      // `app.tsx`.
      void flushBlobs();
      if (settled) {
        // This effect re-runs on the new `fetchedAt` and clears `finding`.
        return;
      }
      if (!showing) {
        return;
      }
      // No answer, and the person is on this page, so say so. The sweep tries
      // again when the library is next on screen.
      showError(
        "That page could not be looked up to get title / picture. Come back here later to try again.",
      );
      setFinding(false);
    });
    return () => {
      showing = false;
    };
    // `item` is a new store reference on every render; these three decide
    // whether there is anything to look up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchedAt, asked]);

  const options: FileOption[] = lists().map((list) => ({
    id: list.id,
    label: listLabel(list),
  }));
  const stored = useBlobUrl(item.thumbHash);
  // A picture address beats a PDF's stored first page; other kinds have only
  // the address.
  const picture = item.thumbUrl ?? stored;
  // A fetch settling mid-word re-renders this page, so each field holds its
  // text in progress and writes it to the synced store when left.
  const title = useEditedText(item.title, (text) => {
    setItemTitle(item, text);
  });
  const note = useEditedText(item.note, (text) => {
    setItemNote(item, text.trim());
  });
  const thumb = useEditedText(item.thumbUrl, (text) => {
    setItemThumb(item, text);
  });
  // The address is kept as typed, so a slip can be fixed in place. A failed
  // load is remembered by its address, as the card does, so a new address is
  // tried.
  const [failed, setFailed] = useState<string | undefined>(undefined);
  const pictureNote =
    item.thumbUrl !== undefined && !isWebAddress(item.thumbUrl)
      ? "That isn't a web address, so there's no picture."
      : picture !== undefined && picture === failed
        ? "That address didn't give a picture."
        : undefined;
  return (
    <Page className="page-column">
      <fieldset>
        <legend>Link</legend>
        <div class="top">
          <a class="address" href={item.url} target="_blank" rel="noreferrer">
            {item.url}
          </a>
          <ToucanButton
            disabled={finding}
            onClick={() => {
              refetchItem(item);
              setAsked(asked + 1);
            }}
          >
            {finding ? (
              <>
                <ToucanSpinner class="looking" />
                Fetching…
              </>
            ) : (
              "Fetch again"
            )}
          </ToucanButton>
        </div>
        <input
          type="text"
          aria-label="Title"
          placeholder="Title"
          value={title.text}
          onInput={title.onInput}
          onChange={title.onDone}
        />
        {item.refused === true && (
          // A site behind bot protection refuses the fetch, so the card has
          // only what the address gave it, and the person fills in the rest.
          <p class="muted">
            This page could not be read, so you&rsquo;ll need to set the name
            and picture yourself.
          </p>
        )}
        {item.failed !== undefined && (
          <p class="muted">{item.failed} Fetch again to try once more.</p>
        )}
        <textarea
          ref={noteField}
          rows={3}
          aria-label="Note"
          placeholder="Note"
          value={note.text}
          onInput={note.onInput}
          onChange={note.onDone}
        />
      </fieldset>
      <fieldset class="filed">
        <legend>List</legend>
        <ToucanFilePicker
          options={options}
          chosen={item.listId ?? null}
          label="List"
          noneLabel="Misc"
          newLabel="＋ New list"
          onPick={(listId) => {
            setItemList(item, listId);
          }}
          onNew={() => {
            setNewList(true);
          }}
        />
        <div class="switches">
          {/* A switch rather than a button, since archived is a state, and a
              switch shows which way it is set. */}
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              checked={isArchived(item)}
              onChange={(event) => {
                if (event.currentTarget.checked) {
                  archiveItem(item, Date.now());
                } else {
                  unarchiveItem(item);
                }
              }}
            />
            Archived
          </label>
          {/* Whether the page needs a subscription decides where Open goes
              and whether it is fetched. The list of paywalled publishers only
              guesses, so the person can override it here. */}
          <label class="switch">
            <input
              type="checkbox"
              role="switch"
              checked={item.paywalled === true}
              onChange={(event) => {
                setItemPaywalled(item, event.currentTarget.checked);
              }}
            />
            Paywalled
          </label>
        </div>
      </fieldset>
      <fieldset>
        <legend>Picture</legend>
        <input
          type="url"
          aria-label="Picture address"
          placeholder="Picture address"
          value={thumb.text}
          autocapitalize="off"
          autocorrect="off"
          spellcheck={false}
          onInput={thumb.onInput}
          onChange={thumb.onDone}
        />
        {pictureNote !== undefined ? (
          <p class="muted">{pictureNote}</p>
        ) : (
          picture !== undefined && (
            /* A picture address is hotlinked, not stored, and sends no
               referrer. */
            <img
              class="thumb"
              alt=""
              src={picture}
              loading="lazy"
              referrerpolicy="no-referrer"
              onError={() => {
                setFailed(picture);
              }}
            />
          )
        )}
      </fieldset>
      <fieldset>
        <legend>Dates</legend>
        <dl class="facts">
          {itemFacts(item).map((fact) => (
            <Fragment key={fact.label}>
              <dt>{fact.label}</dt>
              <dd>{fact.value}</dd>
            </Fragment>
          ))}
        </dl>
      </fieldset>
      {newList && (
        <ToucanNewThingDialog
          thing="list"
          placeholder="Papers, Recipes, Long reads…"
          reserved={RESERVED_LIST_NAMES}
          taken={takenNames(lists())}
          onSave={(name, emoji) => {
            // A list made from this page is where this item was headed, so it
            // is filed there rather than being asked for twice.
            const created =
              emoji === ""
                ? addList(store(), name)
                : addList(store(), name, emoji);
            setItemList(item, created.id);
          }}
          onClose={() => {
            setNewList(false);
          }}
        />
      )}
      <ToucanDangerZone>
        <ToucanButton
          variant="danger"
          onClick={() => {
            // Deleted at once with an Undo, not after a confirmation, as in the
            // other apps. See `deleteWithUndo`.
            deleteWithUndo(
              store().items,
              item,
              `Deleted ${item.title}`,
              () => {
                deleteItem(store(), item.id);
                go(libraryPath());
              },
              () => undefined,
            );
          }}
        >
          Delete article
        </ToucanButton>
      </ToucanDangerZone>
    </Page>
  );
}
